import React, {useEffect, useRef, useState} from 'react'
import {useAppSelector} from 'src/redux/storeHooks'

type StepStatus = 'done' | 'active' | 'pending'

export type InitialSyncProgressDetailSnapshot = {
  display_name: string
  remaining_qty: number
  total_qty_fetched: number
  total_qty_available_in_channel: number
}

export type InitialSyncCompletionSnapshot = {
  platformId: number | null
  platformDisplayName: string
  storeIdentifier: string
  progressDetail: InitialSyncProgressDetailSnapshot[]
  productsFetchedCount: number | null
}

interface SyncStep {
  label: string
  status: StepStatus
  fetched?: number | null
  total?: number | null
  remaining?: number | null
}

const BANNER_SHELL_CLASS = 'mx-auto mt-3'
const BANNER_SHELL_STYLE: React.CSSProperties = {
  maxWidth: '94%',
  background: '#f8f9fa',
  border: '1px solid #dee2e6',
  borderRadius: 8,
  padding: '12px 16px',
}

const StepIcon: React.FC<{status: StepStatus}> = ({status}) => {
  if (status === 'done') {
    return (
      <span style={{color: '#28a745', fontSize: 14, width: 20, display: 'inline-block'}}>✓</span>
    )
  }

  if (status === 'active') {
    return (
      <span style={{width: 20, display: 'inline-block'}}>
        <span
          className='spinner-border spinner-border-sm'
          style={{width: 12, height: 12, borderWidth: 2, color: '#6c757d'}}
        />
      </span>
    )
  }

  return <span style={{width: 20, display: 'inline-block', color: '#adb5bd'}}>⌛</span>
}

const DoneCheck: React.FC = () => (
  <span style={{color: '#28a745', fontSize: 14, width: 20, display: 'inline-block'}}>✓</span>
)

const AnimatedCount: React.FC<{target: number}> = ({target}) => {
  const [displayed, setDisplayed] = useState(0)
  const prevTarget = useRef(0)

  useEffect(() => {
    if (target === prevTarget.current) return

    const start = prevTarget.current
    const diff = target - start
    if (diff <= 0) {
      setDisplayed(target)
      prevTarget.current = target
      return
    }

    const duration = Math.min(1200, diff * 0.5)
    const startTime = performance.now()
    let raf: number

    const step = (now: number) => {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplayed(Math.round(start + diff * eased))

      if (progress < 1) {
        raf = requestAnimationFrame(step)
      } else {
        prevTarget.current = target
      }
    }

    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [target])

  return <>{displayed.toLocaleString()}</>
}

const formatResourceCountLabel = (displayName: string, count: number): string => {
  const lower = displayName.trim().toLowerCase()
  if (count === 1) {
    return `${count.toLocaleString()} ${lower}`
  }
  if (lower === 'location') {
    return `${count.toLocaleString()} locations`
  }
  if (lower.endsWith('s')) {
    return `${count.toLocaleString()} ${lower}`
  }
  return `${count.toLocaleString()} ${lower}s`
}

const buildImportedResourceRows = (
  snapshot: InitialSyncCompletionSnapshot
): {displayName: string; count: number}[] => {
  const rows: {displayName: string; count: number}[] = []
  const seen = new Set<string>()

  snapshot.progressDetail.forEach((item) => {
    const key = item.display_name.trim().toLowerCase()
    if (seen.has(key)) return
    if (typeof item.total_qty_fetched !== 'number') return
    seen.add(key)
    rows.push({displayName: item.display_name, count: Math.max(item.total_qty_fetched, 0)})
  })

  const hasProducts = seen.has('products')
  if (
    !hasProducts &&
    typeof snapshot.productsFetchedCount === 'number' &&
    !Number.isNaN(snapshot.productsFetchedCount)
  ) {
    rows.push({
      displayName: 'Products',
      count: Math.max(snapshot.productsFetchedCount, 0),
    })
  }

  return rows
}

type InitialSyncProgressBannerProps =
  | {variant?: 'progress'}
  | {
      variant: 'complete'
      snapshot: InitialSyncCompletionSnapshot
      onDismiss: () => void
    }

const InitialSyncProgressBannerInProgress: React.FC = () => {
  const {initialSyncProgressDetail, initialSyncSteps, initialPlatformSyncMessage} = useAppSelector(
    (state) => state.auth
  )

  const bannerMessage =
    initialPlatformSyncMessage?.trim() ||
    'Setting up your store - this may take a few minutes'

  const hasProgressDetail = initialSyncProgressDetail.length > 0
  const steps: SyncStep[] = hasProgressDetail
    ? [
        {label: 'Store connected', status: 'done'},
        ...initialSyncProgressDetail.map((item) => ({
          label: `Fetching ${item.display_name}`,
          status: item.remaining_qty > 0 ? 'active' : ('done' as StepStatus),
          fetched: item.total_qty_fetched,
          total: item.total_qty_available_in_channel,
          remaining: item.remaining_qty,
        })),
      ]
    : [
        {
          label: 'Store connected',
          status: initialSyncSteps.storeConnected ? 'done' : 'active',
        },
        {
          label: 'Fetching Products',
          status: 'active',
          fetched: initialSyncSteps.productsFetched,
          total: null,
          remaining: null,
        },
        {
          label: 'Fetching Inventory',
          status: initialSyncSteps.inventoryFetched === true ? 'done' : 'pending',
        },
        {
          label: 'Fetching Store Settings',
          status: initialSyncSteps.settingsFetched === true ? 'done' : 'pending',
        },
      ]

  const renderProgress = (step: SyncStep) => {
    if (step.fetched === null || step.fetched === undefined) return null

    const fetched = Math.max(step.fetched, 0)
    const total = step.total !== null && step.total !== undefined ? Math.max(step.total, 0) : null
    const remaining =
      step.remaining !== null && step.remaining !== undefined ? Math.max(step.remaining, 0) : null

    return (
      <span className='ms-1 text-muted'>
        (
        <AnimatedCount target={fetched} />
        {total !== null ? ` of ${total.toLocaleString()}` : ''} fetched
        {remaining !== null && remaining > 0 ? `, ${remaining.toLocaleString()} remaining` : ''})
      </span>
    )
  }

  return (
    <div className={BANNER_SHELL_CLASS} style={BANNER_SHELL_STYLE}>
      <p
        className='mb-2 fw-semibold fs-7 text-dark'
        dangerouslySetInnerHTML={{__html: bannerMessage}}
      />
      <div className='d-flex flex-column gap-1'>
        {steps.map((step, index) => (
          <div
            key={index}
            className='d-flex align-items-center'
            style={{
              fontSize: 13,
              color: step.status === 'pending' ? '#adb5bd' : '#212529',
              transition: 'color 0.4s ease',
            }}
          >
            <StepIcon status={step.status} />
            <span className='ms-1'>
              {step.label}
              {renderProgress(step)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

const InitialSyncProgressBannerComplete: React.FC<{
  snapshot: InitialSyncCompletionSnapshot
  onDismiss: () => void
}> = ({snapshot, onDismiss}) => {
  const resourceRows = buildImportedResourceRows(snapshot)
  const platformLabel = snapshot.platformDisplayName.trim() || 'Store'
  const storePart = snapshot.storeIdentifier.trim()
  const headingSuffix = storePart ? `${platformLabel} (${storePart})` : platformLabel

  return (
    <div className={BANNER_SHELL_CLASS} style={BANNER_SHELL_STYLE}>
      <div className='d-flex justify-content-between align-items-start mb-2 gap-2'>
        <p className='mb-0 fw-semibold fs-7 text-dark flex-grow-1' style={{fontSize: 13}}>
          <DoneCheck />
          <span className='ms-1'>Initial import complete — {headingSuffix}</span>
        </p>
        <button
          type='button'
          className='btn-close flex-shrink-0'
          aria-label='Close'
          onClick={onDismiss}
        />
      </div>
      <div className='d-flex flex-column gap-1'>
        <div className='d-flex align-items-center' style={{fontSize: 13, color: '#212529'}}>
          <DoneCheck />
          <span className='ms-1'>Store connected</span>
        </div>
        {resourceRows.map((row) => (
          <div
            key={row.displayName}
            className='d-flex align-items-center'
            style={{fontSize: 13, color: '#212529'}}
          >
            <DoneCheck />
            <span className='ms-1'>
              {row.displayName} fetched{' '}
              <span className='text-muted'>
                — {formatResourceCountLabel(row.displayName, row.count)}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

const InitialSyncProgressBanner: React.FC<InitialSyncProgressBannerProps> = (props) => {
  if (props.variant === 'complete') {
    return (
      <InitialSyncProgressBannerComplete snapshot={props.snapshot} onDismiss={props.onDismiss} />
    )
  }
  return <InitialSyncProgressBannerInProgress />
}

export default InitialSyncProgressBanner
